import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { Icon } from './Icon.js';
import JellyfishLoader from './JellyfishLoader';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import {
  getBiomarkerSelectFields,
  enrichBiomarkersWithRanges,
  getBiomarkerStatus,
  shortenUrl,
  formatBiomarkerValueForDisplay
} from '../utils/biomarkerUtils';
import BiomarkerProgressBar from './BiomarkerProgressBar';
import './LabsTab.css';

function LabsTab({ userId, isOwnProfile = true }) {
  const location = useLocation();
  const [biomarkers, setBiomarkers] = useState([]);
  const [biomarkerResults, setBiomarkerResults] = useState([]);
  const [panelResults, setPanelResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPanelId, setSelectedPanelId] = useState('latest');
  const [notTestedCollapsed, setNotTestedCollapsed] = useState(false);
  const [highlightedBiomarkerId, setHighlightedBiomarkerId] = useState(null);
  const [userGender, setUserGender] = useState(null);
  const [uploadData, setUploadData] = useState({
    panelName: '',
    testDate: '',
    results: []
  });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [deletingResultId, setDeletingResultId] = useState(null);

  // Load biomarkers and user's results
  useEffect(() => {
    loadBiomarkerData();
  }, [userId]);

  // Check for highlight parameter in URL (could be in hash or search params)
  useEffect(() => {
    // Check both location.search and window.location.hash for the highlight parameter
    const hash = window.location.hash;
    const hashMatch = hash.match(/[?&]highlight=([^&]+)/);
    const highlightId = hashMatch ? hashMatch[1] : new URLSearchParams(location.search).get('highlight');
    
    if (highlightId && biomarkers.length > 0) {
      // Convert to number if biomarker IDs are numbers, otherwise keep as string
      const biomarkerId = biomarkers.find(b => String(b.id) === String(highlightId))?.id;
      
      if (biomarkerId) {
        setHighlightedBiomarkerId(String(biomarkerId));
        
        // Scroll to the highlighted card after a delay to ensure DOM is ready and tab is visible
        setTimeout(() => {
          const cardElement = document.getElementById(`biomarker-card-${biomarkerId}`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Remove highlight after 3 seconds
            setTimeout(() => {
              setHighlightedBiomarkerId(null);
            }, 3000);
          }
        }, 300);
      }
    }
  }, [location.search, location.hash, biomarkers]);

  const loadBiomarkerData = async () => {
    // Declare variables outside try block so they're accessible in catch
    let biomarkersData = null;
    let profile = null;
    
    try {
      setLoading(true);
      
      // Validate userId before making queries
      if (!userId) {
        // Still load biomarkers catalog even if userId is missing
        const biomarkersCacheKey = getCacheKey('fetchBiomarkers', 'all');
        biomarkersData = await dedupeRequest(biomarkersCacheKey, async () => {
          const { data, error } = await supabase
            .from('biomarkers')
            .select(getBiomarkerSelectFields())
            .order('name');
          if (error) throw error;
          return data;
        });

        setBiomarkers(biomarkersData || []);
        setPanelResults([]);
        setBiomarkerResults([]);
        return;
      }
      
      // Fetch user profile to get gender
      const genderCacheKey = getCacheKey('fetchUserGender', userId);
      const profileData = await dedupeRequest(genderCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('gender')
          .eq('user_id', userId)
          .maybeSingle();
        if (error) throw error;
        return data;
      });
      
      profile = profileData;
      if (profile) {
        setUserGender(profile.gender);
      }
      
      // Load all available biomarkers
      const biomarkersCacheKey = getCacheKey('fetchBiomarkers', 'all');
      biomarkersData = await dedupeRequest(biomarkersCacheKey, async () => {
        const { data, error } = await supabase
          .from('biomarkers')
          .select(getBiomarkerSelectFields())
          .order('name');
        if (error) throw error;
        return data;
      });

      // Load user's panel results - only if userId is valid
      let panelData = null;
      try {
        const panelResultsCacheKey = getCacheKey('fetchBiomarkerPanelResults', userId);
        panelData = await dedupeRequest(panelResultsCacheKey, async () => {
          const { data, error } = await supabase
            .from('biomarker_panel_results')
            .select(`
              *,
              biomarker_results (
                *,
                biomarkers (*)
              )
            `)
            .eq('user_id', userId)
            .order('test_date', { ascending: false });
          if (error) throw error;
          return data;
        });
      } catch (panelError) {
        // Don't throw - still set biomarkers catalog even if panels fail
        // This allows the UI to show biomarkers even if RLS blocks the nested query
        panelData = null;
      }

      if (!panelData) {
        setPanelResults([]);
        setBiomarkerResults([]);
      } else {
        setPanelResults(panelData || []);
        
        // Flatten biomarker results for easier access
        const allResults = [];
        panelData?.forEach(panel => {
          panel.biomarker_results?.forEach(result => {
            allResults.push({
              ...result,
              panel: panel,
              biomarker: result.biomarkers
            });
          });
        });
        setBiomarkerResults(allResults);
      }

      // Always set biomarkers catalog, even if panel query failed
      // Enrich biomarkers with gender-specific ranges
      const enrichedBiomarkers = enrichBiomarkersWithRanges(biomarkersData || [], profile?.gender);
      setBiomarkers(enrichedBiomarkers);

    } catch (error) {
      // Still try to set biomarkers catalog if we have it
      if (biomarkersData) {
        const enrichedBiomarkers = enrichBiomarkersWithRanges(biomarkersData || [], profile?.gender);
        setBiomarkers(enrichedBiomarkers);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLatestResult = (biomarkerId) => {
    return biomarkerResults
      .filter(result => result.biomarker_id === biomarkerId)
      .sort((a, b) => new Date(b.panel.test_date) - new Date(a.panel.test_date))[0];
  };

  const getFilteredResults = () => {
    if (selectedPanelId === 'latest') {
      return biomarkerResults;
    }
    return biomarkerResults.filter(result => result.panel.id === selectedPanelId);
  };

  const getResultForBiomarker = (biomarkerId) => {
    const filteredResults = getFilteredResults();
    return filteredResults
      .filter(result => result.biomarker_id === biomarkerId)
      .sort((a, b) => new Date(b.panel.test_date) - new Date(a.panel.test_date))[0];
  };

  const getPanelOptions = () => {
    const options = [
      { value: 'latest', label: 'Latest' }
    ];
    
    // Group panels by date and add them to options
    const panelsByDate = panelResults
      .sort((a, b) => new Date(b.test_date) - new Date(a.test_date))
      .map(panel => ({
        value: panel.id,
        label: `${panel.panel_name} - ${new Date(panel.test_date).toLocaleDateString()}`
      }));
    
    return [...options, ...panelsByDate];
  };

  const getBiomarkerStatusLocal = (biomarker, result) => {
    if (!result || !biomarker) return 'unknown';
    
    const value = result.value;
    const status = getBiomarkerStatus(value, biomarker, userGender);
    
    // Map to expected status values for this component
    if (status === 'critical') return 'critical';
    if (status === 'normal') return 'optimal';
    return 'unknown';
  };

  const handleDeleteBiomarkerResult = async (result) => {
    if (!result?.id) return;
    try {
      setDeletingResultId(result.id);
      const { error } = await supabase
        .from('biomarker_results')
        .delete()
        .eq('id', result.id);
      if (error) throw error;
      await loadBiomarkerData();
    } catch (err) {
      console.error('Error deleting biomarker result:', err);
    } finally {
      setDeletingResultId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return '#10b981'; // green
      case 'warning': return '#f59e0b'; // yellow
      case 'critical': return '#ef4444'; // red
      default: return '#6b7280'; // gray
    }
  };

  const handleUploadSubmit = async () => {
    try {
      // Create panel result
      const { data: panelData, error: panelError } = await supabase
        .from('biomarker_panel_results')
        .insert({
          user_id: userId,
          panel_name: uploadData.panelName,
          lab_name: null,
          test_date: uploadData.testDate
        })
        .select()
        .single();

      if (panelError) throw panelError;

      // Create biomarker results
      const resultsToInsert = uploadData.results.map(result => ({
        panel_id: panelData.id,
        biomarker_id: result.biomarker_id,
        value: parseFloat(result.value)
      }));

      const { error: resultsError } = await supabase
        .from('biomarker_results')
        .insert(resultsToInsert);

      if (resultsError) throw resultsError;

      // Reload data
      await loadBiomarkerData();
      setShowUploadModal(false);
      setUploadData({
        panelName: '',
        testDate: '',
        results: []
      });

    } catch (error) {
      alert('Error uploading panel data. Please try again.');
    }
  };

  const addResultRow = () => {
    setUploadData(prev => ({
      ...prev,
      results: [...prev.results, { biomarker_id: '', value: '' }]
    }));
  };

  const updateResultRow = (index, field, value) => {
    setUploadData(prev => ({
      ...prev,
      results: prev.results.map((result, i) => 
        i === index ? { ...result, [field]: value } : result
      )
    }));
  };

  const removeResultRow = (index) => {
    setUploadData(prev => ({
      ...prev,
      results: prev.results.filter((_, i) => i !== index)
    }));
  };

  // Check if user has health data consent
  const checkHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const { data, error } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking health data consent:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking health data consent:', error);
      return false;
    }
  };

  // Store health data consent
  const storeHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const consentText = "I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.";
      const consentTextVersion = "v1.0-2026-01-12";

      const { error } = await supabase
        .from('health_data_consent')
        .insert({
          user_id: userId,
          consent_type: 'health_biometric_processing',
          consent_text_version: consentTextVersion,
          consent_text_snapshot: consentText,
          given_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error storing health data consent:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing health data consent:', error);
      return false;
    }
  };

  // Handle Add Panel Data button click - check consent first
  const handleAddPanelDataClick = async () => {
    if (!userId) {
      setShowUploadModal(true);
      return;
    }

    try {
      const userHasConsented = await checkHealthDataConsent(userId);
      
      if (!userHasConsented) {
        setShowConsentModal(true);
        return;
      }

      // User has consented, proceed with opening upload modal
      setShowUploadModal(true);
    } catch (error) {
      console.error('Error checking consent:', error);
      // On error, allow the user to proceed (fail open)
      setShowUploadModal(true);
    }
  };

  // Handle consent modal actions
  const handleConsent = async () => {
    try {
      if (!userId) {
        setShowConsentModal(false);
        return;
      }

      // Store consent in database
      const consentStored = await storeHealthDataConsent(userId);
      
      if (consentStored) {
        setShowConsentModal(false);
        // Open the upload modal after consent is given
        setShowUploadModal(true);
      } else {
        alert('Error storing consent. Please try again.');
      }
    } catch (error) {
      console.error('Error handling consent:', error);
      alert('Error storing consent. Please try again.');
    }
  };

  const handleNoConsent = () => {
    // Just close the modal, nothing changes
    setShowConsentModal(false);
  };

  if (loading) {
    return (
      <div className="labs-tab">
        <div className="labs-loading">
          <JellyfishLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="labs-tab">
      <div className="labs-header">
        <div className="labs-action-group">
          {isOwnProfile && (
            <button 
              className="labs-upload-btn"
              onClick={handleAddPanelDataClick}
            >
              <Icon name="Plus" className="labs-upload-icon" />
              Add Panel Data
            </button>
          )}
          <div className="labs-panel-selector">
            <select 
              value={selectedPanelId}
              onChange={(e) => setSelectedPanelId(e.target.value)}
              className="labs-panel-dropdown"
            >
              {getPanelOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="labs-content">
        {biomarkers.length === 0 ? (
          <div className="labs-empty">
            <Icon name="TestTube" className="labs-empty-icon" />
            <h3>No biomarkers available</h3>
            <p>Contact your administrator to set up biomarker definitions.</p>
          </div>
        ) : (() => {
          const testedBiomarkers = biomarkers.filter(biomarker => getResultForBiomarker(biomarker.id));
          const untestedBiomarkers = biomarkers.filter(biomarker => !getResultForBiomarker(biomarker.id));
          
          return (
            <div className="biomarker-sections">
              {/* Biomarkers with measurements */}
              <div className="biomarker-section">
                {testedBiomarkers.length === 0 ? (
                  <div className="section">
                    <div className="empty-state">
                      No tested biomarkers yet.
                    </div>
                  </div>
                ) : (
                  <div className="biomarker-grid">
                    {testedBiomarkers.map(biomarker => {
                      const result = getResultForBiomarker(biomarker.id);
                      const status = getBiomarkerStatusLocal(biomarker, result);

                      return (
                        <BiomarkerCard
                          key={biomarker.id}
                          biomarker={biomarker}
                          result={result}
                          status={status}
                          userGender={userGender}
                          isHighlighted={highlightedBiomarkerId === String(biomarker.id)}
                          onDelete={isOwnProfile ? handleDeleteBiomarkerResult : null}
                          isDeleting={result && deletingResultId === result.id}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Biomarkers without measurements */}
              {untestedBiomarkers.length > 0 && (
                <div className="biomarker-section">
                  <div className="biomarker-section-header">
                    <div className="biomarker-section-title-group">
                      <h3 className="biomarker-section-title">Not-tested Biomarkers</h3>
                      <button 
                        className="biomarker-section-toggle"
                        onClick={() => setNotTestedCollapsed(!notTestedCollapsed)}
                      >
                        <Icon name={notTestedCollapsed ? "ChevronDown" : "ChevronUp"} />
                      </button>
                    </div>
                  </div>
                      {!notTestedCollapsed && (
                    <div className="biomarker-grid">
                      {untestedBiomarkers.map(biomarker => (
                        <BiomarkerCard
                          key={biomarker.id}
                          biomarker={biomarker}
                          result={null}
                          status="unknown"
                          userGender={userGender}
                          isHighlighted={highlightedBiomarkerId === String(biomarker.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Upload Modal */}
      {showUploadModal && isOwnProfile && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Add Biomarker Panel</h3>
              <button 
                className="labs-upload-modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                <Icon name="X" />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '24px' }}>
              <div className="labs-upload-form-group">
                <label>Panel Name</label>
                <input
                  type="text"
                  value={uploadData.panelName}
                  onChange={(e) => setUploadData(prev => ({ ...prev, panelName: e.target.value }))}
                  placeholder="e.g., Comprehensive Metabolic Panel"
                />
              </div>
              
              <div className="labs-upload-form-group">
                <label>Test Date</label>
                <input
                  type="date"
                  value={uploadData.testDate}
                  onChange={(e) => setUploadData(prev => ({ ...prev, testDate: e.target.value }))}
                />
              </div>

              <div className="labs-upload-results">
                <div className="labs-upload-results-header">
                  <h4>Biomarker Results</h4>
                  <button 
                    className="labs-upload-add-btn"
                    onClick={addResultRow}
                  >
                    <Icon name="Plus" />
                    Add Result
                  </button>
                </div>

                {uploadData.results.map((result, index) => {
                  const selectedBiomarker = biomarkers.find(b => String(b.id) === String(result.biomarker_id));
                  const unit = selectedBiomarker?.unit || '';
                  
                  return (
                    <div key={index} className="labs-upload-result-row">
                      <select
                        value={result.biomarker_id}
                        onChange={(e) => updateResultRow(index, 'biomarker_id', e.target.value)}
                      >
                        <option value="">Select Biomarker</option>
                        {biomarkers.map(biomarker => (
                          <option key={biomarker.id} value={biomarker.id}>
                            {biomarker.name}
                          </option>
                        ))}
                      </select>
                      
                      <input
                        type="number"
                        step="0.01"
                        value={result.value}
                        onChange={(e) => updateResultRow(index, 'value', e.target.value)}
                        placeholder="Value"
                      />
                      
                      <div className="labs-upload-result-row-unit">
                        {unit}
                      </div>
                      
                      <button 
                        className="icon-btn icon-btn--danger icon-btn--sm"
                        onClick={() => removeResultRow(index)}
                        title="Remove result"
                      >
                        <Icon name="Trash2" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button 
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={() => setShowUploadModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleUploadSubmit}
                  disabled={!uploadData.panelName || !uploadData.testDate || uploadData.results.length === 0}
                  style={{ flex: 1 }}
                >
                  Upload Panel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Data Consent Modal */}
      {showConsentModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Health Data Consent</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You are unable to add panel data without clicking the I consent button.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can withdraw this consent at any time in account settings.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleNoConsent}
                  style={{ flex: 1 }}
                >
                  I don't consent
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConsent}
                  style={{ flex: 1 }}
                >
                  I consent
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BiomarkerCard({ biomarker, result, status, userGender, isHighlighted, onDelete, isDeleting }) {
  const hasResult = !!result;
  const showDeleteButton = onDelete && hasResult;
  const displayValue = hasResult ? formatBiomarkerValueForDisplay(result.value) : '';
  const displayUnit = hasResult ? (result.unit_override || biomarker.unit) : biomarker.unit || '';
  const testDate = hasResult ? new Date(result.panel.test_date).toISOString().split('T')[0] : '';

  const handleCopySource = async (e) => {
    e.stopPropagation(); // Prevent tooltip from closing
    if (biomarker.description_source) {
      try {
        await navigator.clipboard.writeText(biomarker.description_source);
        // Optional: Show a brief feedback message
        const originalText = e.target.textContent;
        e.target.textContent = 'Copied!';
        setTimeout(() => {
          e.target.textContent = originalText;
        }, 2000);
      } catch (err) {
        // Failed to copy to clipboard
      }
    }
  };

  const handleCopyRangeSource = async (e) => {
    e.stopPropagation(); // Prevent tooltip from closing
    if (biomarker.range_source) {
      try {
        await navigator.clipboard.writeText(biomarker.range_source);
        // Optional: Show a brief feedback message
        const button = e.target.closest('.biomarker-value-range-wrapper').querySelector('button');
        if (button) {
          const originalText = button.textContent;
          button.textContent = 'Copied!';
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }
      } catch (err) {
        // Failed to copy to clipboard
      }
    }
  };

  return (
    <div 
      id={`biomarker-card-${biomarker.id}`}
      className={`biomarker-card ${!hasResult ? 'no-data' : ''} ${isHighlighted ? 'highlighted' : ''}`}
    >
      <div className="biomarker-card-header">
        <div className="biomarker-card-title">
          <Icon name="TestTube" className="biomarker-card-icon" />
          <h3>
            {biomarker.name}
          </h3>
        </div>
        <div className="biomarker-card-header-right">
          {showDeleteButton && (
            <button
              type="button"
              className="biomarker-card-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(result);
              }}
              disabled={isDeleting}
              title="Delete this biomarker result"
              aria-label="Delete this biomarker result"
            >
              <Icon name="Trash2" className="biomarker-card-delete-icon" />
            </button>
          )}
          {biomarker.description && (
          <div className="biomarker-card-info-wrapper">
            <button className="biomarker-card-info" aria-label="Biomarker information">
              <Icon name="Info" />
              <div className="biomarker-card-tooltip">
                {biomarker.description}
                {biomarker.description_source && (
                  <p>
                    Source:{' '}
                    <span
                      className="biomarker-card-tooltip-link"
                      onClick={handleCopySource}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCopySource(e);
                        }
                      }}
                    >
                      {shortenUrl(biomarker.description_source)}
                    </span>
                  </p>
                )}
              </div>
            </button>
          </div>
          )}
        </div>
      </div>

      <div className="biomarker-card-content">
        <div className="biomarker-value">
          {hasResult && (
            <>
              <span className="biomarker-value-number">{displayValue}</span>
              <span className="biomarker-value-unit">{displayUnit}</span>
            </>
          )}
          {(biomarker.clinical_low !== null || biomarker.clinical_high !== null) && (
            <div className="biomarker-value-range-wrapper">
              <span className="biomarker-value-range">
                Range: {biomarker.clinical_low !== null ? formatBiomarkerValueForDisplay(biomarker.clinical_low) : '•'}-{biomarker.clinical_high !== null ? formatBiomarkerValueForDisplay(biomarker.clinical_high) : '•'}{!hasResult && displayUnit ? ` ${displayUnit}` : ''}
              </span>
              {biomarker.range_source && (
                <div className="biomarker-value-range-tooltip">
                  <p>
                    Source:{' '}
                    <button
                      type="button"
                      onClick={handleCopyRangeSource}
                    >
                      {shortenUrl(biomarker.range_source)}
                    </button>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <BiomarkerProgressBar
          biomarker={biomarker}
          result={result}
          userGender={userGender}
          optimalRange={null}
        />

        <div className="biomarker-divider"></div>

        <div className="biomarker-tags">
          {biomarker.category && (
            <div className="biomarker-category-tag">
              {biomarker.category}
            </div>
          )}
          {testDate && (
            <div className="biomarker-test-tag">
              {testDate}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'optimal': return '#10b981';
    case 'warning': return '#f59e0b';
    case 'critical': return '#ef4444';
    default: return '#6b7280';
  }
}

function adjustColor(color, amount) {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
}

export default LabsTab;